import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { BehaviorSubject, Observable, lastValueFrom, tap } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public showLoader: boolean = false;
  private _hasReserve = false;
  private _hasSplit = false;
  private _hasCryptoDeposit: boolean | null = null;
  private static readonly FF_NEW_PARAM_KEY = 'FF_NEW_PARAM';
  private _ffCustomerBalance$ = new BehaviorSubject<boolean>(false);
  public readonly featureFlagNewParam$ = this._ffCustomerBalance$.asObservable();
  private _featureFlagNewParam =
    sessionStorage.getItem(AuthService.FF_NEW_PARAM_KEY) === 'true';
  constructor(private http: HttpClient, private router: Router) {
    const savedSplit = sessionStorage.getItem('HAS_SPLIT');
    this._hasSplit = savedSplit === 'true';
    const saved = sessionStorage.getItem('HAS_RESERVE');
    this._hasReserve = saved === 'true';
  }

  private url(path: string): string {
    const base =
      this.getOriginCurrency() !== 'BRL'
        ? environment.intlUrl
        : environment.apiUrl;

    return `${base}${path}`;
  }

  getUser(): string {
    return sessionStorage.getItem('SESSION-USER') ?? '';
  }

  userIsAuth(): boolean {
    return (
      sessionStorage.getItem('SESSION-USER') !== null &&
      this.getAcessToken() !== null &&
      this.getRefreshToken() !== null
    );
  }

  getAcessToken(): string {
    return JSON.parse(this.getUser()).accessToken;
  }

  getRefreshToken(): string {
    return JSON.parse(this.getUser()).refreshToken;
  }

  getName(): string {
    return sessionStorage.getItem('name') ?? '';
  }

  getCompany(): string {
    return sessionStorage.getItem('company') ?? '';
  }

  getOriginCurrency(): string {
    return this.getAcessTokenInformation()?.originCurrency ?? 'BRL';
  }

  loginWithEmail(credentials: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/login`, credentials);
  }

  needChangePassword(): boolean {
    return (
      this.getAcessTokenInformation()!.securityStep == undefined ||
      this.getAcessTokenInformation()!.securityStep.indexOf('SET_PASSWORD') ==
      -1
    );
  }

  needChange2FA(): boolean {
    return (
      this.getAcessTokenInformation()!.securityStep == undefined ||
      this.getAcessTokenInformation()!.securityStep.indexOf('SET_2FA') == -1
    );
  }

  changeSecurityStep(step: string, isAdd: boolean) {
    this.http
      .post<any>(`${environment.apiUrl}/changeStepSecurity`, {
        step: step,
        isAdd: isAdd,
      })
      .subscribe();
  }

  isAdmin(): boolean {
    if (!this.userIsAuth()) {
      return false;
    }
    return this.getAcessTokenInformation()?.role.indexOf('Administrator')! > -1;
  }

  isReadOnly(): boolean {
    if (!this.userIsAuth()) {
      return false;
    }
    return this.getAcessTokenInformation()?.role.indexOf('ReadOnly')! > -1;
  }

  isNoCrypto(): boolean {
    if (!this.userIsAuth()) {
      return false;
    }
    return this.getAcessTokenInformation()?.role.indexOf('NoCrypto')! > -1;
  }

  isCrypto(): boolean {
    if (!this.userIsAuth()) {
      return false;
    }
    return this.getAcessTokenInformation()?.role.indexOf('Crypto')! > -1;
  }

  public isWeb(): boolean {
    return this.getAcessTokenInformation()!.scope === 'WEB';
  }

  callRefreshToken() {
    if (!this.userIsAuth()) {
      return;
    }

    this.http
      .post<any>(`${environment.apiUrl}/token/refresh`, {
        token: this.getRefreshToken(),
      })
      .subscribe({
        next: (user) => {
          this.setUserLoggedIn(user);
        },
        error: async (err) => {
          if (err.status === 401) {
            sessionStorage.removeItem('SESSION-USER');
            await this.router.navigate(['/']);
          }
        },
      });
  }

  revokeToken() {
    if (!this.userIsAuth()) {
      return;
    }
    let email = this.getAcessTokenInformation()!.email;
    lastValueFrom(
      this.http.post<any>(`${environment.apiUrl}/token/revoke`, {
        token: this.getRefreshToken(),
        email: email,
      })
    ).then();
  }

  isCommon(): boolean {
    if (!this.userIsAuth()) return false;
    return this.getAcessTokenInformation()?.role.includes('Common') ?? false;
  }

  clearStorage(): void {
    sessionStorage.clear();
  }

  updatePassword(password: string): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}/users`, {
      password: password,
    });
  }

  public getAccount(): Observable<any> {
    return this.http.get<any>(this.url('/account')).pipe(
      tap(acc => this.setFeatureFlagNewParam(acc?.featureFlagNewParam === true))
    );
  }

  preloadAccountInfo(): void {
    this.http.get<any>(this.url('/account')).subscribe({
      next: acc => {
        this.setReserveAccount(!!acc?.listsAccountTef?.length);
        this.setSplit(acc?.getSplit === true);
        this.setFeatureFlagNewParam(acc?.featureFlagNewParam === true);
      },
      error: () => {
        this.setReserveAccount(false);
        this.setSplit(false);
        this.setFeatureFlagNewParam(false);
      }
    });
  }

  public hasCryptoDeposit(): boolean | null {
    return this._hasCryptoDeposit;
  }

  public setCryptoDeposit(value: boolean): void {
    this._hasCryptoDeposit = value;
  }

  setSplit(value: boolean): void {
    this._hasSplit = value;
    sessionStorage.setItem('HAS_SPLIT', value.toString());
  }

  hasSplit(): boolean {
    return this._hasSplit;
  }

  setReserveAccount(value: boolean): void {
    this._hasReserve = value;
  }

  hasReserveAccount(): boolean {
    return this._hasReserve;
  }

  public validate(email: string, senha: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: 'Basic ' + btoa(`${email}:${senha}`),
    });

    return this.http.head<any>(`${environment.apiUrl}/validate/${email}`, {
      headers,
      observe: 'response',
    });
  }

  needTotp() {
    return sessionStorage.getItem('need2FA') !== null;
  }

  totpValidated() {
    sessionStorage.removeItem('need2FA');
  }

  public setUserLoggedIn(response: any) {
    let token = {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
    };
    sessionStorage.setItem('SESSION-USER', JSON.stringify(token));
    sessionStorage.setItem('name', this.getAcessTokenInformation()!.name);
    sessionStorage.setItem('company', this.getAcessTokenInformation()!.company);
    sessionStorage.setItem('need2FA', 'x');
  }

  resetPassword(email: string): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}/users/reset`, {
      email: email,
    });
  }
  private getAcessTokenInformation(): JwtData | undefined {
    if (this.userIsAuth()) {
      return this.decodeToken(this.getAcessToken());
    }

    return undefined;
  }

  public decodeToken(token: string): JwtData {
    return jwtDecode<JwtData>(token);
  }

  getTotpManualCode(email: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${environment.apiUrl}/totp/code/${email}`)
    );
  }
  getTotpQrCode(email: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${environment.apiUrl}/totp/qrCode/${email}`)
    );
  }

  validateTotpCode(code: string, email?: string): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${environment.apiUrl}/totp/validate-code`, {
        email: email,
        code: code,
      })
    );
  }

  public isCallRefreshToken(): boolean {
    if (this.userIsAuth() && this.getAcessTokenInformation() !== null) {
      const currentTime = Math.floor(Date.now() / 1000);
      const expirationTime = this.getAcessTokenInformation()!.exp;
      const remaingTimeToExpiration = expirationTime - currentTime;
      return remaingTimeToExpiration <= 180;
    }

    return false;
  }

  public getAccountId(): string {
    return this.getAcessTokenInformation()!.accountId;
  }

  private setFeatureFlagNewParam(v: boolean) {
    this._featureFlagNewParam = v;
    this._ffCustomerBalance$.next(v);
    sessionStorage.setItem(AuthService.FF_NEW_PARAM_KEY, String(v));
  }

  hasFeatureFlagNewParam(): boolean {
    return this._featureFlagNewParam;
  }
}

export interface JwtData {
  email: string;
  accountId: string;
  name: string;
  scope: string;
  company: string;
  role: string[];
  securityStep: string[];
  exp: number;
  iss: string;
  aud: string;
  originCurrency: string;
}
